/**
 * JobWorkflow Page
 *
 * Full-page workflow experience for completing a job/permit application.
 * Uses the WorkflowContainer component for the TurboTax-style guided flow.
 *
 * Part of Phase 8: Job Workflow Wizard
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import type { WorkflowStepId } from '@/types/workflow';
import { doc, getDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { WorkflowContainer, type JobData } from '@/components/workflow/WorkflowContainer';
import { dualReadJob, dualWriteJob } from '@/lib/firestore-helpers';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

export default function JobWorkflow() {
  const { id: jobId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { toast } = useToast();

  // Deep link params - navigate to specific step/substep
  const initialStepId = searchParams.get('step') as WorkflowStepId | null;
  const initialSubstepId = searchParams.get('substep');

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [jobData, setJobData] = useState<JobData | null>(null);
  const [contractorId, setContractorId] = useState<string | null>(null);

  // Load job data
  useEffect(() => {
    if (!user || !jobId) return;

    const loadJob = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Get contractorId from user document (like JobDetail does)
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data();
        const cId = userData?.contractorId ||
                   (userData?.isPrimaryAdmin ? user.uid : user.uid);

        setContractorId(cId);

        // Use dualReadJob with correct contractorId
        const jobSnap = await dualReadJob(cId, jobId);

        if (!jobSnap.exists()) {
          setError('Job not found');
          return;
        }

        const data = jobSnap.data();
        setJobData({
          id: jobSnap.id,
          title: data.title || data.name || `Job ${jobId.slice(0, 8)}`,
          description: data.description,
          location: data.location || data.jobAddress || data.address,
          jurisdictionId: data.jurisdictionId,
          jurisdictionCode: data.jurisdictionCode,
          jurisdictionAhjName: data.jurisdictionAhjName || data.jurisdictionName,
          jurisdictionOverlays: data.jurisdictionOverlays,
          permitType: data.permitType,
          jobScope: data.jobScope,
          trade: data.trade || data.permitType,
          estimatedValue: data.estimatedValue || data.projectValue,
          propertyType: data.propertyType,
          contractorRole: data.contractorRole,
          client_name: data.client_name || data.clientName,
          client_email: data.client_email || data.clientEmail,
          client_phone: data.client_phone || data.clientPhone,
          // Include other fields
          ...data,
        });
      } catch (err) {
        console.error('Failed to load job:', err);
        setError(err instanceof Error ? err.message : 'Failed to load job');
      } finally {
        setIsLoading(false);
      }
    };

    loadJob();
  }, [user, jobId]);

  // Debounced job updates - local state updates immediately, Firestore writes are batched
  const pendingUpdatesRef = useRef<Partial<JobData>>({});
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushPendingSaves = useCallback(async () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    const pending = pendingUpdatesRef.current;
    if (Object.keys(pending).length > 0 && user && jobId && contractorId) {
      pendingUpdatesRef.current = {};
      try {
        await dualWriteJob(contractorId, jobId, {
          ...pending,
          updatedAt: serverTimestamp(),
        });
      } catch (err) {
        console.error('Failed to save job:', err);
        toast({
          title: 'Error',
          description: 'Failed to save changes. Please try again.',
          variant: 'destructive',
        });
      }
    }
  }, [user, jobId, contractorId, toast]);

  // Flush pending saves on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
      // Fire-and-forget flush on unmount
      const pending = pendingUpdatesRef.current;
      if (Object.keys(pending).length > 0 && contractorId && jobId) {
        pendingUpdatesRef.current = {};
        dualWriteJob(contractorId, jobId, {
          ...pending,
          updatedAt: serverTimestamp(),
        }).catch((err) => console.error('Failed to flush saves on unmount:', err));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractorId, jobId]);

  const handleJobUpdate = useCallback(
    (updates: Partial<JobData>) => {
      if (!user || !jobId || !contractorId) return;

      // 1. Update local state immediately for responsive typing
      setJobData((prev) => (prev ? { ...prev, ...updates } : null));

      // 2. Accumulate pending updates
      pendingUpdatesRef.current = { ...pendingUpdatesRef.current, ...updates };

      // 3. Debounce the Firestore write (1 second after last keystroke)
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
      saveTimerRef.current = setTimeout(async () => {
        saveTimerRef.current = null;
        const pending = pendingUpdatesRef.current;
        pendingUpdatesRef.current = {};

        try {
          await dualWriteJob(contractorId, jobId, {
            ...pending,
            updatedAt: serverTimestamp(),
          });
        } catch (err) {
          console.error('Failed to update job:', err);
          toast({
            title: 'Error',
            description: 'Failed to save changes. Please try again.',
            variant: 'destructive',
          });
        }
      }, 1000);
    },
    [user, jobId, contractorId, toast]
  );

  // Handle compliance re-check
  const handleRerunCompliance = useCallback(async () => {
    if (!user || !jobId || !contractorId) return;

    try {
      const runComplianceCheck = httpsCallable(functions, 'runComplianceCheck');
      await runComplianceCheck({ jobId });

      toast({
        title: 'Compliance check complete',
        description: 'The compliance check has been updated.',
      });

      // Reload job data to get updated compliance results using dualReadJob
      const jobSnap = await dualReadJob(contractorId, jobId);
      if (jobSnap.exists()) {
        const data = jobSnap.data();
        setJobData((prev) => (prev ? { ...prev, ...data } : null));
      }
    } catch (err) {
      console.error('Compliance check failed:', err);
      toast({
        title: 'Error',
        description: 'Compliance check failed. Please try again.',
        variant: 'destructive',
      });
    }
  }, [user, jobId, contractorId, toast]);

  // Handle exit - flush pending saves then navigate to jobs list
  const handleExit = useCallback(async () => {
    await flushPendingSaves();
    navigate('/jobs');
  }, [flushPendingSaves, navigate]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">Loading workflow...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !jobData || !user || !jobId || !contractorId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <p className="text-destructive">{error || 'Unable to load workflow'}</p>
          <Button onClick={() => navigate('/jobs')}>Return to Jobs</Button>
        </div>
      </div>
    );
  }

  return (
    <WorkflowContainer
      jobId={jobId}
      contractorId={contractorId}
      jobData={jobData}
      onExit={handleExit}
      onJobUpdate={handleJobUpdate}
      onRerunCompliance={handleRerunCompliance}
      // Deep link support - navigate to specific step/substep
      initialStepId={initialStepId || undefined}
      initialSubstepId={initialSubstepId || undefined}
    />
  );
}
